// Automatisches Speichern von Notizen (19.10.2026, Wunsch Dilsar): Nichts geht verloren, ohne dass man „Speichern“ klicken muss.
// Ein Wert (Text) wird gesetzt; kurz danach (Standard 900 ms Ruhe) speichert der AutoSpeicher von selbst. Außerdem sofort auf
// Anforderung (`jetzt()`: Feld verlassen, Klick irgendwo anders, Seitenwechsel, Auflegen, „Nächster Anruf“, „Speichern“).
// Läuft gerade ein Speichern, wartet ein weiteres darauf und schickt danach den neuesten Stand (nie zwei gleichzeitig, nie ein
// älterer Text nach einem neueren). Fehlgeschlagen = Stand „fehler“, der Wert bleibt offen und der nächste Versuch schickt ihn erneut.
// Reine Logik ohne Browser (testbar mit Node, werkzeuge/tests/autospeichern.test.mjs).

export type SpeicherStand = "gespeichert" | "offen" | "speichert" | "fehler";

export interface AutoSpeicherOptionen<T> {
  /** Sind zwei Werte gleich (dann gibt es nichts zu speichern)? */
  gleich: (a: T, b: T) => boolean;
  /** Das eigentliche Speichern; wirft bei Fehlern. */
  speichern: (wert: T) => Promise<void>;
  /** true = dieser Wert wird nie gespeichert (z. B. leere Notiz, die eine vorhandene nicht überschreiben soll). */
  nichtSpeichern?: (wert: T) => boolean;
  verzoegerungMs?: number;
  /** Stand hat sich geändert (für die kleine Anzeige „Gespeichert“) */
  beiStand?: (stand: SpeicherStand) => void;
  /** Speichern ist fehlgeschlagen */
  beiFehler?: (fehler: unknown) => void;
}

export class AutoSpeicher<T> {
  private zuletzt: T;
  private aktuell: T;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private laufend: Promise<void> | null = null;
  private verworfen = false;
  stand: SpeicherStand = "gespeichert";

  private readonly opt: AutoSpeicherOptionen<T>;

  constructor(start: T, opt: AutoSpeicherOptionen<T>) {
    this.opt = opt;
    this.zuletzt = start;
    this.aktuell = start;
  }

  private setzeStand(s: SpeicherStand): void {
    if (this.stand === s) return;
    this.stand = s;
    this.opt.beiStand?.(s);
  }

  /** Gibt es etwas, das noch nicht gespeichert ist? */
  get geaendert(): boolean {
    return !this.opt.gleich(this.aktuell, this.zuletzt);
  }

  /** Neuer Wert (Tippen): Speichern in `verzoegerungMs` einplanen. */
  setze(wert: T): void {
    this.aktuell = wert;
    this.planStoppen();
    if (this.verworfen) return;
    if (!this.geaendert) {
      if (!this.laufend) this.setzeStand("gespeichert");
      return;
    }
    this.setzeStand(this.laufend ? "speichert" : "offen");
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.jetzt();
    }, this.opt.verzoegerungMs ?? 900);
  }

  /** Alles Weitere verwerfen (Eintrag wurde gelöscht): es wird nichts mehr gespeichert. */
  verwerfen(): void {
    this.verworfen = true;
    this.planStoppen();
  }

  private planStoppen(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  /** Sofort speichern, falls etwas offen ist. true = alles gesichert (oder nichts zu tun), false = fehlgeschlagen. */
  async jetzt(): Promise<boolean> {
    this.planStoppen();
    while (this.laufend) await this.laufend;
    if (this.verworfen || !this.geaendert) return true;
    if (this.opt.nichtSpeichern?.(this.aktuell)) {
      this.setzeStand("gespeichert");
      return true;
    }
    const wert = this.aktuell;
    this.setzeStand("speichert");
    let ok = true;
    this.laufend = (async () => {
      try {
        await this.opt.speichern(wert);
        this.zuletzt = wert;
      } catch (e) {
        ok = false;
        this.opt.beiFehler?.(e);
      }
    })();
    await this.laufend;
    this.laufend = null;
    if (!ok) {
      this.setzeStand("fehler");
      return false;
    }
    // Währenddessen weitergetippt: der neueste Stand folgt gleich hinterher (kein älterer Text nach einem neueren)
    if (this.geaendert) return this.jetzt();
    this.setzeStand("gespeichert");
    return true;
  }
}
